import React, { useState, useMemo } from 'react';

import { LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { MultipleValue, SelectValue } from '@shared/table/Rules/component';
import TableIndex from '@shared/table/TableIndex';
import type { TableService, SupportRule } from '@shared/table/TableIndex';
import { Badge, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';

interface MerchantOption {
  label: string;
  value: string;
  [key: string]: unknown;
}

interface ProductLabelTemplatesListProps {
  role?: 'admin' | 'operator';
  service: TableService;
  merchantOptions?: MerchantOption[];
  hideMerchantUI?: boolean;
}

const ProductLabelTemplatesList: React.FC<ProductLabelTemplatesListProps> = ({
  role = 'admin',
  service,
  merchantOptions = [],
  hideMerchantUI = false, // Operator single-merchant 模式使用
}) => {
  const { t } = useTranslation();
  const [lastModified, setLastModified] = useState(0);

  const urlPrefix = role === 'operator' ? '/operator' : '/admin';

  const columns = useMemo(() => {
    const cols: Array<Record<string, unknown>> = [
      {
        nameKey: `models.product_label_template.name`,
        persist: true,
        sortable: false,
        width: '200px',
        wrap: true,
        cell: ({ id, name }: { id: string; name: string }) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/product_label_templates/${id}`} style={{ wordBreak: 'break-word' }}>
              {name}
            </Link>
            <a
              href={`${urlPrefix}/product_label_templates/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      },
    ];

    // Add merchant column for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      cols.push({
        nameKey: `models.product_label_template.merchant_name`,
        persist: true,
        sortable: false,
        width: '150px',
        wrap: true,
        selector: ({ merchant_name }: { merchant_name?: string }) => merchant_name || '-',
      });
    }

    cols.push(
      {
        nameKey: `models.product_label_template.paper_size_label`,
        width: '220px',
        wrap: true,
        selector: ({ paper_size_label }: { paper_size_label?: string }) => paper_size_label || '-',
      },
      {
        name: (
          <span style={{ whiteSpace: 'nowrap' }}>
            <Space size={4}>
              {t('models.product_label_template.is_default')}
              <Tooltip title={t('shared.product_label_templates.list.column_tooltips.is_default')}>
                <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
              </Tooltip>
            </Space>
          </span>
        ),
        width: '110px',
        cell: ({ is_default }: { is_default?: boolean }) => {
          if (is_default) {
            return <Badge status="success" text={t('shared.product_label_templates.list.is_default_true')} />;
          }
          return <span style={{ color: '#999' }}>-</span>;
        },
      },
      {
        nameKey: `models.product_label_template.description`,
        sortable: false,
        minWidth: '200px',
        grow: 3,
        wrap: true,
        selector: ({ description }: { description?: string }) => description || '-',
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'product_label_templates.created_at',
        minWidth: '170px',
        grow: 1,
        cell: ({ created_at }: { created_at?: string }) => (
          <span style={{ whiteSpace: 'nowrap' }}>
            {created_at ? formatDateTime(created_at) : '-'}
          </span>
        ),
      }
    );

    return cols;
  }, [role, urlPrefix, t, hideMerchantUI]);

  const supportRules = useMemo(() => {
    const rules: SupportRule[] = [
      {
        label: t(`models.product_label_template.name`),
        component: MultipleValue,
        inputName: 'product_label_templates.name',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.product_label_templates.list.filter_tooltips.name.title'),
          content: t('shared.product_label_templates.list.filter_tooltips.name.content'),
        },
      },
    ];

    // Add merchant filter for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      rules.push({
        label: t(`models.inventory.merchant_id`),
        component: MultipleValue,
        inputName: 'merchants.id',
        defaultPinned: true,
        createable: false,
        defaultOptions: merchantOptions,
        tooltip: {
          title: t('shared.product_label_templates.list.filter_tooltips.merchant.title'),
          content: t('shared.product_label_templates.list.filter_tooltips.merchant.content'),
        },
      });
    }

    rules.push(
      {
        label: t(`models.product_label_template.is_default`),
        component: SelectValue,
        inputName: 'product_label_templates.is_default',
        defaultOptions: [
          {
            label: t(`shared.product_label_templates.list.is_default_true`),
            value: 'true',
          },
          {
            label: t(`shared.product_label_templates.list.is_default_false`),
            value: 'false',
          },
        ],
        tooltip: {
          title: t('shared.product_label_templates.list.filter_tooltips.is_default.title'),
          content: t('shared.product_label_templates.list.filter_tooltips.is_default.content'),
        },
      }
    );

    return rules;
  }, [role, t, merchantOptions, hideMerchantUI]);

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'contains',
        searchFields: [
          t('models.product_label_template.name'),
          t('models.product_label_template.description'),
        ],
      }}
      columnsEditable={true}
      tableId={`${role === 'operator' ? 'Operator' : 'Admin'}ProductLabelTemplatesTable`}
      defaultQuery={{
        sorting_field: 'product_label_templates.id',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      service={service}
      filterHelpExample={t('shared.product_label_templates.list.filter_help_example')}
      filterDocsUrl={`/docs/${role}/product_label_templates/list#features`}
    />
  );
};

export default ProductLabelTemplatesList;
